import { useState, useCallback, useEffect } from "react";
import { useAppSelector } from "@/redux/store";
import CatalogService from "@/services/catalogService";
import debounce from "lodash/debounce";
import { MultiSelectOption, Source } from "../types";

export const useServerSearch = (
  filter: {
    queryParam: string;
    search?: {
      enabled?: boolean;
      source?: Source;
    };
  },
  searchValue: string
) => {
  const pageData = useAppSelector(state => state.dynamicPage.data);
  const isSearchable = !!filter.search?.enabled;
  const isServerSearch =
    isSearchable && filter.search?.source === Source.SERVER;

  const [serverOptions, setServerOptions] = useState<MultiSelectOption[]>(null);

  const sendSearchRequest = useCallback(
    debounce(async (query: string) => {
      try {
        const body = {
          url: pageData?.url,
          filter: { queryParam: filter.queryParam, searchText: query }
        };
        const response = await CatalogService.getFilterSearchData(body);
        setServerOptions(response?.data ?? []);
      } catch {
        setServerOptions(null);
      }
    }, 350),
    [pageData?.url, filter.queryParam]
  );

  useEffect(() => {
    if (!isServerSearch) {
      return;
    }

    const q = searchValue.trim();
    if (!q) {
      setServerOptions(null);
      sendSearchRequest.cancel();
      return;
    }

    sendSearchRequest(q);

    return () => {
      sendSearchRequest.cancel();
    };
  }, [searchValue, sendSearchRequest, isServerSearch]);

  return {
    isServerSearch,
    serverOptions,
    isSearchable
  };
};
